import React from 'react';
import { Box, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Label, Input, Button } from '../../Common';
import './PageHeader.css';

export interface PageHeaderProps {
  /**
   * Page title
   */
  title: string;
  
  /**
   * Show search input
   */
  showSearch?: boolean;
  
  /**
   * Search placeholder text
   */
  searchPlaceholder?: string;
  
  /**
   * Search value
   */
  searchValue?: string;
  
  /**
   * Search onChange handler
   */
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  
  /**
   * Action button text
   */
  actionButtonText?: string;

  /**
   * Action button style primary or secondary
   */
  primaryButton?: boolean;
  
  /**
   * Action button click handler
   */
  onActionClick?: () => void;

  /**
   * Show icon-only button (MoreHoriz icon)
   */
  showIconButton?: boolean;

  /**
   * Icon button click handler
   */
  onIconButtonClick?: (event: React.MouseEvent<HTMLElement>) => void;
  
  /**
   * Additional className
   */
  className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  showSearch = false,
  searchPlaceholder = 'Search',
  searchValue = '',
  onSearchChange,
  actionButtonText,
  primaryButton = true,
  onActionClick,
  showIconButton = false,
  onIconButtonClick,
  className = '',
}) => {
  return (
    <Box className={`page-header ${className}`}>
      {/* Title */}
      <Label variant="h1">
        {title}
      </Label>

      {/* Search and Action Button */}
      {(showSearch || actionButtonText || showIconButton) && (
        <Box className="page-header-actions">
          {/* Search Input */}
          {showSearch && (
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={onSearchChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '20px', color: 'var(--palette-light-text-secondary-value-hex)' }} />
                    </InputAdornment>
                  ),
                },
              }}
              className="page-header-search"
            />
          )}

          {/* Icon Button */}
          {showIconButton && (
            <IconButton
              onClick={onIconButtonClick}
              sx={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                border: '1px solid var(--palette-light-brand-primary-value-hex)',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                },
              }}
            >
              <MoreHorizIcon sx={{ fontSize: '20px', color: 'var(--palette-light-brand-primary-value-hex)' }} />
            </IconButton>
          )}

          {/* Action Button */}
          {actionButtonText && (
            primaryButton ? (
              <Button primary onClick={onActionClick}>
                {actionButtonText}
              </Button>
            ) : (
              <Button bordered primary={false} onClick={onActionClick}>
                {actionButtonText}
              </Button>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;

